import { type Dispatch, type SetStateAction, useState } from "react";
import { Activity, ClipboardList, Plus, RefreshCcw, ShieldAlert, Trash2 } from "lucide-react";
import type { EventScopeType, GlobalEvent, Severity, WorldData } from "../../../domain/types";
import { defaultEffectValue, scopeOptions, type GmDraft, type GmEffectType } from "../../gmEvents";
import { EventList } from "../../components/EventList";
import { Select } from "../../components/Panel";
import { formatAirspaceStatus, formatEffect, formatEventEffectType, formatEventStatus, formatScope, formatScopeType, formatScopeValue, formatSeverity } from "../../format";
import { FilterAll, SEVERITY_VALUES } from "./gmTypes";
import { matchesQuery } from "./GmShared";


export function GmEventForm({
  world,
  draft,
  setDraft,
  onSubmit
}: {
  world: WorldData;
  draft: GmDraft;
  setDraft: Dispatch<SetStateAction<GmDraft>>;
  onSubmit: () => void;
}) {
  const scopeValues = scopeOptions(world, draft.scopeType);
  let effectValueControl = (
    <label>
      效果参数
      <input value={draft.effectValue} onChange={(event) => setDraft((current) => ({ ...current, effectValue: event.target.value }))}/>
    </label>
  );
  if (draft.effectType === "airspace_status") {
    effectValueControl = (
      <Select
        label="效果参数"
        value={normalizedAirspaceStatus(draft.effectValue)}
        values={["open", "restricted", "closed"]}
        formatValue={formatAirspaceStatus}
        onChange={(effectValue) => setDraft((current) => ({ ...current, effectValue }))}
      />
    );
  }

  return (
    <div className="gm-form">
      <label>
        事件标题
        <input value={draft.title} onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))} />
      </label>
      <Select
        label="等级"
        value={draft.severity}
        values={["info", "watch", "warning", "critical"]}
        formatValue={(value) => formatSeverity(value as Severity)}
        onChange={(severity) => setDraft((current) => ({ ...current, severity: severity as Severity }))}
      />
      <Select
        label="作用域"
        value={draft.scopeType}
        values={["global", "region", "airport", "airspace", "fuel_zone"]}
        formatValue={(value) => formatScopeType(value as EventScopeType)}
        onChange={(scopeType) =>
          setDraft((current) => {
            const typedScope = scopeType as EventScopeType;
            return { ...current, scopeType: typedScope, scopeValue: scopeOptions(world, typedScope)[0] ?? "global" };
          })
        }
      />
      <Select
        label="目标"
        value={selectedScopeValue(draft.scopeValue, scopeValues)}
        values={scopeValues}
        formatValue={(value) => formatScopeValue(world, draft.scopeType, value)}
        onChange={(scopeValue) => setDraft((current) => ({ ...current, scopeValue }))}
      />
      <Select
        label="效果"
        value={draft.effectType}
        values={["demand_multiplier", "fuel_price_delta", "airspace_status", "airport_capacity_multiplier", "license_pressure", "reputation_delta", "maintenance_disruption"]}
        formatValue={(value) => formatEventEffectType(value as GmEffectType)}
        onChange={(effectType) =>
          setDraft((current) => ({
            ...current,
            effectType: effectType as GmEffectType,
            effectValue: defaultEffectValue(effectType as GmEffectType)
          }))
        }
      />
      {effectValueControl}
      <label>
        持续小时
        <input
          type="number"
          min={0}
          max={168}
          value={draft.durationHours}
          onChange={(event) => setDraft((current) => ({ ...current, durationHours: Number(event.target.value) }))}
        />
      </label>
      <label>
        原因
        <input value={draft.reason} onChange={(event) => setDraft((current) => ({ ...current, reason: event.target.value }))} />
      </label>
      <label className="check-row">
        <input type="checkbox" checked={draft.activateNow} onChange={(event) => setDraft((current) => ({ ...current, activateNow: event.target.checked }))} />
        立即激活
      </label>
      <button className="primary-command" onClick={onSubmit}>
        <ShieldAlert size={16} />
        下发 GM 事件
      </button>
    </div>
  );
}

function selectedScopeValue(currentValue: string, scopeValues: string[]): string {
  if (scopeValues.includes(currentValue)) {
    return currentValue;
  }
  return scopeValues[0] ?? "global";
}

function normalizedAirspaceStatus(value: string): string {
  if (["open", "restricted", "closed"].includes(value)) {
    return value;
  }
  return "closed";
}

export function EventCatalog({
  world,
  events,
  onActivate,
  onCancel,
  onRefresh
}: {
  world: WorldData;
  events: GlobalEvent[];
  onActivate: (eventId: string) => void;
  onCancel: (eventId: string) => void;
  onRefresh: () => void;
}) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<FilterAll<GlobalEvent["status"]>>("all");
  const [severity, setSeverity] = useState<FilterAll<Severity>>("all");
  const filteredEvents = events.filter((event) => {
    return (
      (status === "all" || event.status === status) &&
      (severity === "all" || event.severity === severity) &&
      matchesQuery([event.title, event.reason, event.scopeType, event.scopeValue, formatScope(world, event.scopeType, event.scopeValue), event.effects.map(formatEffect).join(" ")], query)
    );
  });

  return (
    <div className="table-wrap">
      <div className="gm-filter-bar table-toolbar">
        <label>
          事件筛选
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="标题/原因/范围/效果" />
        </label>
        <label>
          状态
          <select value={status} onChange={(event) => setStatus(event.target.value as FilterAll<GlobalEvent["status"]>)}>
            <option value="all">全部</option>
            <option value="draft">草稿</option>
            <option value="active">生效中</option>
            <option value="cancelled">已取消</option>
            <option value="expired">已过期</option>
          </select>
        </label>
        <label>
          等级
          <select value={severity} onChange={(event) => setSeverity(event.target.value as FilterAll<Severity>)}>
            <option value="all">全部</option>
            {SEVERITY_VALUES.map((item) => (
              <option key={item} value={item}>
                {formatSeverity(item)}
              </option>
            ))}
          </select>
        </label>
        <button onClick={onRefresh}>
          <RefreshCcw size={14} />
          刷新事件库
        </button>
      </div>
      <table>
        <thead>
          <tr>
            <th>状态</th>
            <th>事件</th>
            <th>范围</th>
            <th>效果</th>
            <th>原因</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {filteredEvents.map((event) => (
            <tr key={event.id}>
              <td>
                <span className={`severity ${event.severity}`}>{formatEventStatus(event.status)}</span>
              </td>
              <td>{event.title}</td>
              <td>{formatScope(world, event.scopeType, event.scopeValue)}</td>
              <td>{event.effects.map(formatEffect).join("; ")}</td>
              <td>{event.reason}</td>
              <td>
                <div className="row-actions">
                  <button disabled={event.status === "active"} onClick={() => onActivate(event.id)}>
                    激活
                  </button>
                  <button disabled={event.status === "cancelled"} onClick={() => onCancel(event.id)}>
                    取消
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {filteredEvents.length === 0 && (
            <tr>
              <td colSpan={6}>没有匹配的 GM 事件。</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
